import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { Button, Stack } from "@mui/material";
import { useState } from "react";
import { Link as RouterLink } from "react-router";
import type { WorkflowDefinition, WorkflowTemplateVersionStatus } from "../../../../api/types/workflowTypes";
import useClientContext from "../../../../context/useClientContext";
import useUserContext from "../../../../context/useUserContext";
import { pageRoutes } from "../../../../routes";
import UpdateWorkflowDefinitionStatusDialog from "./UpdateWorkflowDefinitionStatusDialog";

interface Props {
  workflowDefinition: WorkflowDefinition;
  onWorkflowDefinitionUpdate: (updatedDefinition: WorkflowDefinition) => void;
}

const WorkflowDefinitionActions = ({ workflowDefinition, onWorkflowDefinitionUpdate }: Props) => {
  const { clientCode, hasAnyPermission } = useClientContext();
  const { isEntriliaUser } = useUserContext();

  const [updatingStatus, setUpdatingStatus] = useState<WorkflowTemplateVersionStatus>();

  const hasAccessToWorkflows = hasAnyPermission(["ViewWorkflows", "ManageWorkflows"]);
  const hasAccessToWorkflowActivation = hasAnyPermission(["ManageClientIntegrations"]);

  const isWorkflowActivationDisabled = workflowDefinition.workflowType === "InvoiceProcessing";

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        {workflowDefinition.status === "Active" && hasAccessToWorkflows && !!workflowDefinition.templateVersionId && (
          <Button
            component={RouterLink}
            to={`/${clientCode}/${pageRoutes.workflows}/${pageRoutes.workflowTemplates}/${workflowDefinition.templateVersionId}`}
            variant="outlined"
            color="secondary"
            startIcon={<SettingsIcon />}
            target="_blank"
            rel="noopener noreferrer"
          >
            Template
          </Button>
        )}

        {workflowDefinition.status === "Active" && hasAccessToWorkflowActivation && isEntriliaUser && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setUpdatingStatus("Inactive")}
            disabled={isWorkflowActivationDisabled}
          >
            Deactivate
          </Button>
        )}

        {workflowDefinition.status === "Inactive" && hasAccessToWorkflowActivation && isEntriliaUser && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setUpdatingStatus("Active")}
            disabled={isWorkflowActivationDisabled}
          >
            Activate
          </Button>
        )}
      </Stack>
      {updatingStatus && (
        <UpdateWorkflowDefinitionStatusDialog
          targetStatus={updatingStatus}
          workflowDefinition={workflowDefinition}
          onClose={() => setUpdatingStatus(undefined)}
          onUpdated={onWorkflowDefinitionUpdate}
        />
      )}
    </>
  );
};

export default WorkflowDefinitionActions;
